import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Loading from '../loading';
import { getTask } from '../../../services';
import { Urls } from '../../../config/urls';
import './styles.scss';

export const LEGAL_DOCUMENT_DIALOG_PAPER_CLASS = 'legal-document-dialog-paper';

const LEGAL_DOCUMENT_FIT_STYLE = `
<style id="legal-doc-fit">
  html {
    box-sizing: border-box !important;
  }
  *, *::before, *::after {
    box-sizing: inherit !important;
  }
  html, body {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
  }
  body,
  body.doc-content,
  .doc-content {
    max-width: 100% !important;
    padding: 16px !important;
  }
  @media (min-width: 769px) {
    body,
    body.doc-content,
    .doc-content {
      max-width: 840px !important;
      margin: 0 auto !important;
      padding: 24px 32px !important;
    }
  }
</style>
`;

interface LegalDocumentViewerProps {
  documentType: 'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY';
  country?: string;
  language?: string;
  contentUrl?: string;
  fallback?: ReactNode;
}

interface LegalDocumentResponse {
  versionId: number;
  documentType: 'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY';
  versionCode: string;
  country?: string | null;
  language: string;
  contentUrl: string;
}

const withFitStyles = (html: string): string => {
  if (html.includes('id="legal-doc-fit"')) {
    return html;
  }
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${LEGAL_DOCUMENT_FIT_STYLE}</head>`);
  }
  return `${LEGAL_DOCUMENT_FIT_STYLE}${html}`;
};

const LegalDocumentViewer = ({ documentType, country, language = 'en', contentUrl, fallback }: LegalDocumentViewerProps) => {
  const [resolvedContentUrl, setResolvedContentUrl] = useState<string | undefined>(contentUrl);
  const [srcDoc, setSrcDoc] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const queryUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('language', language);
    if (country) {
      params.set('country', country);
    }
    return `${Urls.api.currentLegalDocuments}?${params.toString()}`;
  }, [country, language]);

  useEffect(() => {
    if (contentUrl) {
      setResolvedContentUrl(contentUrl);
      return;
    }

    let cancelled = false;

    const fetchDocumentMeta = async () => {
      setLoading(true);
      try {
        const { response } = await getTask({ url: queryUrl });
        if (cancelled) {
          return;
        }

        const document = (response || []).find((item: LegalDocumentResponse) => item.documentType === documentType);
        setResolvedContentUrl(document?.contentUrl);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDocumentMeta();

    return () => {
      cancelled = true;
    };
  }, [contentUrl, documentType, queryUrl]);

  useEffect(() => {
    if (!resolvedContentUrl) {
      setSrcDoc(undefined);
      return;
    }

    let cancelled = false;

    const loadHtml = async () => {
      setLoading(true);
      setSrcDoc(undefined);
      try {
        const response = await fetch(resolvedContentUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch legal document: ${response.status}`);
        }
        const html = await response.text();
        if (!cancelled) {
          setSrcDoc(withFitStyles(html));
        }
      } catch {
        if (!cancelled) {
          // CORS or network failure: fall back to direct iframe src.
          setSrcDoc(undefined);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadHtml();

    return () => {
      cancelled = true;
    };
  }, [resolvedContentUrl]);

  if (loading) {
    return <Loading />;
  }

  if (!resolvedContentUrl) {
    return <>{fallback}</>;
  }

  return (
    <iframe
      className="legal-document-viewer"
      title={`${documentType.toLowerCase()}-viewer`}
      {...(srcDoc ? { srcDoc } : { src: resolvedContentUrl })}
    />
  );
};

export default LegalDocumentViewer;
